import {
  EventApp,
  getTrackingDispatchers,
  TrackingEvent,
} from '@owc/connection-aem-tracking';

import UtilsService from './utils.service';
import {
  TrackableVehicle,
  VehicleTrackingInfo,
  TrackingEventName,
  EventTrackingInfo,
  UrlUtmParams,
} from '../models/entities/tracking.model';

import {
  TRACKING_EVENT_TYPE,
  TRACKING_VEHICLE_SUBBRAND_SEPARATOR,
  TRACKING_PAGE_NAME,
  TRACKING_PAGE_TYPE,
  VEHICLE_TYPE,
  TRACKING_EVENT_ACTION,
  TRACKING_EVENT_LABEL,
} from '../models/constants';
import { useStore } from '../composables/store.composable';

import { Group } from '../graphql/schema';

declare global {
  interface Window {
    otbs: any;
  }
}

export default class TrackingService {
  static async track(event: TrackingEvent): Promise<void> {
    let dispatchers;
    if (window.aemNamespace) {
      dispatchers = await getTrackingDispatchers();
      await dispatchers.pushTrackingEvent(event);
    }
    registerTracking(event);
  }

  static buildApplication(): EventApp {
    const { id, version, parentApplicationId } = window.otbs.build;
    return {
      id,
      version,
      parentApplicationId,
    };
  }

  static buildEvent(name: TrackingEventName, eventDetail: any): TrackingEvent {
    eventDetail.name = eventDetail.name || eventDetail.category;

    return {
      event: name,
      application: TrackingService.buildApplication(),
      eventDetail,
    };
  }

  static buildVehicle(vehicle: TrackableVehicle): VehicleTrackingInfo {
    const {
      baumuster,
      classId,
      bodyStyleId,
      transmission = undefined,
      fuelType = undefined,
      type = VEHICLE_TYPE.CAR,
      vehicleTypes = [],
      modelCode = undefined,
      category = undefined,
    } = vehicle;
    return UtilsService.stripUndefinedValuesFromObject({
      baumusterId: baumuster,
      class: classId,
      bodyType: bodyStyleId,
      transmission,
      fuelType,
      type,
      modelCode,
      category,
      subBrand: TrackingService.formatVehicleSubBrand(vehicleTypes),
    });
  }

  static trackPageView(name: TRACKING_PAGE_NAME): void {
    const { state } = useStore();
    const vehicle = this.getBuildVehicle();
    let event = {
      event: TRACKING_EVENT_TYPE.PAGEVIEW,
      application: TrackingService.buildApplication(),
      page: {
        name,
        type: TRACKING_PAGE_TYPE.TEST_DRIVE,
        retailerId: state.otbcFlow.dealer || state.otbcFlow.selectedDealer?.id,
        vehicle,
      },
    };
    event = UtilsService.cleanObject(event);
    this.track(event);
  }

  static trackEvent(eventTrackingInfo: EventTrackingInfo): void {
    const { state } = useStore();
    const vehicle = this.getBuildVehicle();
    const eventDetail = {
      ...eventTrackingInfo,
      vehicle,
      retailerId: state.otbcFlow.selectedDealer?.id,
    };
    let buildedEvent = this.buildEvent(TRACKING_EVENT_TYPE.CLICK, eventDetail);
    buildedEvent = UtilsService.cleanObject(buildedEvent);
    this.track(buildedEvent);
  }

  static hasEventBeenCalled(
    action: TRACKING_EVENT_ACTION,
    label?: TRACKING_EVENT_LABEL | string
  ): boolean {
    const events: TrackingEvent[] = window.otbs.dataLayer;
    if (!events) return false;
    const eventsByAction = events.filter(
      (event) => event?.eventDetail?.action === action
    );
    if (!eventsByAction.length) return false;
    if (!label) return true;
    const eventsByLabel = eventsByAction.filter(
      (event) => event?.eventDetail?.label === label
    );
    return !!eventsByLabel.length;
  }

  static getBuildVehicle(): VehicleTrackingInfo {
    const { state } = useStore();
    let vehicleInfo: TrackableVehicle = {
      classId: state.otbcFlow.model.classId,
      bodyStyleId: state.otbcFlow.model.bodyStyleId,
      baumuster: this.getVehicleBaumuster(),
      modelCode: state.otbcFlow.model.id,
      fuelType:
        state.otbcFlow.selectedVehicle?.fuelType ||
        state.otbcFlow.selectedFuelType,
      transmission:
        state.otbcFlow.selectedVehicle?.transmission ||
        state.otbcFlow.selectedTransmission,
      vehicleTypes: state.otbcFlow.model.vehicleTypeOptions,
      type: this.getVehicleType(state.otbcFlow.model.group),
      category: this.formatVehicleCategory(state.otbcFlow.model.categories),
    };
    vehicleInfo = UtilsService.removeEmptyStringsFromObject(vehicleInfo);
    return this.buildVehicle(vehicleInfo);
  }

  static getVehicleType(modelGroup: Group): VEHICLE_TYPE {
    return modelGroup === Group.VAN ? VEHICLE_TYPE.VAN : VEHICLE_TYPE.CAR;
  }

  static getVehicleBaumuster(): VEHICLE_TYPE {
    const { state } = useStore();
    return (
      state.otbcFlow.selectedVehicle?.baumuster ||
      state.otbcFlow.selectedMotorisation?.baumuster ||
      state.otbcFlow.model?.motorisations[0]?.baumuster.substring(0, 4)
    );
  }

  static formatVehicleCategory(categories: string[]): string | void {
    if (!categories?.length) return;
    categories = categories.map((category) => category.toLowerCase());
    const category =
      categories.length > 1 ? categories.join('|') : categories?.[0];
    return category;
  }

  static formatVehicleSubBrand(vehicleTypes: string[]): string | undefined {
    return vehicleTypes.length > 0
      ? vehicleTypes
          .map((type: string) => type.toLowerCase())
          .sort()
          .join(TRACKING_VEHICLE_SUBBRAND_SEPARATOR)
      : undefined;
  }

  static buildUrlSearchParams(): UrlUtmParams {
    const urlSearchParams = {
      utm_source: UtilsService.getUrlSearchVariable('utm_source') || null,
      utm_medium: UtilsService.getUrlSearchVariable('utm_medium') || null,
      utm_campaign: UtilsService.getUrlSearchVariable('utm_campaign') || null,
      utm_term: UtilsService.getUrlSearchVariable('utm_term') || null,
      utm_content: UtilsService.getUrlSearchVariable('utm_content') || null,
    };
    return UtilsService.cleanObject(urlSearchParams);
  }

  static getUtmParameters(): string | null {
    const { state } = useStore();
    const urlUtmParameters = TrackingService.buildUrlSearchParams();
    const keyValueSplitter =
      state.configuration.trackingSplitters
        ?.trackingParameterKeyValueSplitter || '=';
    const parametersSplitter =
      state.configuration.trackingSplitters?.trackingParameterSplitter || '&';
    if (Object.keys(urlUtmParameters).length !== 0) {
      const utmParameters = Object.keys(urlUtmParameters)
        .map((key) =>
          urlUtmParameters[key]
            ? `${key}${keyValueSplitter}${urlUtmParameters[key]}`
            : ''
        )
        .join(parametersSplitter);

      return utmParameters;
    }
    const csrefUtmParameters = state.csref;
    if (csrefUtmParameters) {
      return csrefUtmParameters;
    }

    const prospectUtmParameters =
      state.prospect?.customerOtherSet?.utmParameters;
    if (prospectUtmParameters) {
      return prospectUtmParameters;
    }

    return null;
  }
}

export function registerTracking(payload: TrackingEvent): void {
  if (!window.otbs.dataLayer) {
    window.otbs = {
      ...(window.otbs || {}),
      dataLayer: [],
    };
  }
  window.otbs.dataLayer.push(payload);
}
